import {
	IconAtom2Outline18 as Atom,
	IconGlobeOutline18 as Globe,
	IconLockOutline18 as Lock,
} from "nucleo-ui-outline-18"

// `UNLISTED` is the stored name of the Agent tier; see the note in
// @productlane/lib/src/article-visibility.
export type ArticleVisibility = "PUBLIC" | "UNLISTED" | "INTERNAL"

export const VISIBILITY_ICONS: Record<ArticleVisibility, typeof Globe> = {
	PUBLIC: Globe,
	UNLISTED: Atom,
	INTERNAL: Lock,
}

export interface ArticleVisibilityOption {
	value: ArticleVisibility
	label: string
	description: string
}

export const ARTICLE_VISIBILITY_OPTIONS: ArticleVisibilityOption[] = [
	{
		value: "PUBLIC",
		label: "Public",
		description: "Shown on your docs site.",
	},
	{
		value: "UNLISTED",
		label: "Agent",
		// Parallel with the Internal line below, so the two AI tiers read as a
		// contrast. Both stay short enough for one line in the menu.
		description: "Only the AI agent uses it.",
	},
	{
		value: "INTERNAL",
		label: "Internal",
		description: "Only the AI Assistant reads it.",
	},
]

export const getArticleVisibilityLabel = (visibility: ArticleVisibility) =>
	ARTICLE_VISIBILITY_OPTIONS.find((option) => option.value === visibility)
		?.label ?? "Public"

// Icon names, not components: these land in the article's `icon` column, which
// LucideIcon resolves by string.
const DEFAULT_ICONS: Record<ArticleVisibility, string> = {
	PUBLIC: "Page2",
	UNLISTED: "Atom",
	INTERNAL: "Lock",
}

export const getDefaultArticleIcon = (visibility: ArticleVisibility) =>
	DEFAULT_ICONS[visibility] ?? DEFAULT_ICONS.PUBLIC

// A hidden article never reaches the docs site, so "Publish" overstates it.
export const getGoLiveButtonLabel = (visibility: ArticleVisibility) =>
	visibility === "PUBLIC" ? "Publish" : "Save"

// Cover and summary only ever render on the docs site, which hidden articles
// skip.
export const showsCoverImage = (visibility: ArticleVisibility) =>
	visibility === "PUBLIC"

export const showsSummary = (visibility: ArticleVisibility) =>
	visibility === "PUBLIC"

// One pill per row, never two: an unpublished hidden article reads as a draft
// first, because it reaches nobody yet.
export type ArticlePillState = "draft" | "agent" | "internal" | "none"

export const getArticlePillState = (article: {
	published: boolean
	visibility: ArticleVisibility
	kind?: "DOC" | "LINK" | null
}): ArticlePillState => {
	// A link has nothing to publish, so neither pill says anything true about it.
	if (article.kind === "LINK") return "none"
	if (!article.published) return "draft"
	if (article.visibility === "INTERNAL") return "internal"
	return article.visibility === "UNLISTED" ? "agent" : "none"
}
